import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUser, updateUser, getCompanies, deleteUser } from '../services/api';
import { getAdminInfo } from '../services/auth';
import './UserDetail.css';

function UserDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [editForm, setEditForm] = useState({ name: '', address: '', phone: '', company_id: null });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteReason, setDeleteReason] = useState('');

  const adminInfo = getAdminInfo();

  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getUser(id);
      setUser(data.user);
      setEditForm({
        name: data.user.name || '',
        address: data.user.address || '',
        phone: data.user.phone || '',
        company_id: data.user.company_id ?? null,
      });
    } catch (err) {
      setError(err.message || t('admin.user_detail.errors.load_user_failed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const loadCompanies = useCallback(async () => {
    try {
      const data = await getCompanies();
      setCompanies(data.companies || []);
    } catch {
      setCompanies([]);
    }
  }, []);

  useEffect(() => {
    loadUser();
    loadCompanies();
  }, [loadUser, loadCompanies]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await updateUser(id, {
        name: editForm.name || null,
        address: editForm.address || null,
        phone: editForm.phone || null,
        company_id: editForm.company_id ?? undefined,
      });
      await loadUser();
    } catch (err) {
      setError(err.message || t('admin.user_detail.errors.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError('');
      if (!adminInfo?.id) {
        throw new Error('Admin-ID fehlt (bitte erneut einloggen).');
      }
      await deleteUser(id, adminInfo.id, {
        confirm: deleteConfirm,
        confirm_email: deleteConfirmEmail,
        reason: deleteReason || null,
      });
      navigate('/admin/users');
    } catch (err) {
      setError(err.message || t('admin.user_detail.errors.delete_failed'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="loading">{t('admin.user_detail.loading')}</div>;
  }

  if (error && !user) {
    return <div className="error">{error}</div>;
  }

  if (!user) {
    return <div className="error">{t('admin.user_detail.not_found')}</div>;
  }

  return (
    <div className="user-detail">
      <h2>{t('admin.user_detail.title')}</h2>
      {error && <div className="error-message">{error}</div>}
      <div className="detail-card">
        <div className="detail-row">
          <strong>{t('admin.user_detail.fields.user_id')}:</strong> {user.id}
        </div>
        <div className="detail-row">
          <strong>{t('admin.user_detail.fields.email')}:</strong> {user.email}
        </div>
        <div className="detail-row">
          <strong>{t('admin.user_detail.fields.name')}:</strong>
          <input
            type="text"
            value={editForm.name}
            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
            className="detail-input"
          />
        </div>
        <div className="detail-row">
          <strong>{t('admin.user_detail.fields.mandantennummer')}:</strong> {user.mandantennummer || '-'}
        </div>
        <div className="detail-row">
          <strong>{t('admin.user_detail.fields.company')}:</strong>
          <select
            value={editForm.company_id ?? ''}
            onChange={(e) => setEditForm((f) => ({ ...f, company_id: e.target.value ? Number(e.target.value) : null }))}
            className="detail-input"
          >
            <option value="">{t('admin.user_detail.company_none')}</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.mandantennummer})
              </option>
            ))}
          </select>
        </div>
        <div className="detail-row">
          <strong>{t('admin.user_detail.fields.address')}:</strong>
          <input
            type="text"
            value={editForm.address}
            onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
            className="detail-input"
          />
        </div>
        <div className="detail-row">
          <strong>{t('admin.user_detail.fields.phone')}:</strong>
          <input
            type="text"
            value={editForm.phone}
            onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
            className="detail-input"
          />
        </div>
        <div className="detail-row">
          <strong>{t('admin.user_detail.fields.status')}:</strong>
          <span className={`badge ${user.is_approved ? 'badge-success' : 'badge-warning'}`}>
            {user.is_approved ? t('admin.common.status.approved') : t('admin.common.status.pending')}
          </span>
        </div>
        <div className="detail-row" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
          <strong style={{ color: '#b91c1c' }}>{t('common.delete')}</strong>
          <div style={{ marginTop: '8px', width: '100%' }}>
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.9rem' }}>
              <input
                type="checkbox"
                checked={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.checked)}
                disabled={deleting}
              />
              Ich bestätige das Löschen
            </label>
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '6px' }}>
                Sicherheitsabfrage: bitte E-Mail exakt eingeben
              </div>
              <input
                type="email"
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                placeholder={user.email}
                className="detail-input"
                disabled={deleting}
              />
            </div>
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '6px' }}>
                Grund (optional)
              </div>
              <input
                type="text"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="z. B. Dublette / Testkonto"
                className="detail-input"
                disabled={deleting}
              />
            </div>
            <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#6b7280' }}>
              Hinweis: Wenn eine Lizenz ausgestellt/zugeordnet wurde, ist Löschen gesperrt.
            </div>
          </div>
        </div>
        <div className="detail-actions">
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? t('admin.common.buttons.saving') : t('admin.common.buttons.save')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={deleting || !deleteConfirm || !deleteConfirmEmail.trim()}
            title="Benutzer löschen (archiviert 6 Monate)"
          >
            {deleting ? t('admin.common.buttons.saving') : t('common.delete')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/users')}>
            {t('admin.user_detail.back_to_list')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserDetail;






